import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

/** One finding of the Transformer error analysis (app/transformers/framework/diagnostics.py). */
export interface TransformerErrorFinding {
  category: string;
  title: string;
  where: string;
  problem: string;
  fix: string;
  rulePosition?: number | null;
  explainsError: boolean;
  examples: string[];
}

/** The analysis Flask sends with a failed Transformer run (status fatal_error, field `analysis`). */
export interface TransformerErrorAnalysis {
  transformer: string;
  /** Optional title; default "<transformer> could not run" (the pre-run check says "cannot run yet"). */
  title?: string;
  transformType: number;
  failedRule?: { position: number; ruleName?: string } | null;
  headline: string;
  engineIssue: boolean;
  reportTo?: string | null;
  findings: TransformerErrorFinding[];
  technical: { errorType?: string; errorMessage?: string; rule?: any; transformType?: number; trace?: string };
}

/**
 * The Transformer error popup (docs/Transformer_Error_Analysis.md): where the problem is,
 * what is wrong and how to fix it, in place of the raw engine error. Black background,
 * orange text (panelClass 'warning-dialog').
 */
@Component({
  selector: 'app-transformer-error-dialog',
  templateUrl: './transformer-error-dialog.component.html',
  styleUrls: ['./transformer-error-dialog.component.css'],
  standalone: false
})
export class TransformerErrorDialogComponent {
  copied = false;

  constructor(@Inject(MAT_DIALOG_DATA) public analysis: TransformerErrorAnalysis,
              public dialogRef: MatDialogRef<TransformerErrorDialogComponent>) { }

  get title(): string {
    return this.analysis?.title || `${this.analysis?.transformer || 'Transformer'} could not run`;
  }

  /** Technical details exist only for a failed run (the pre-run check has none). */
  get hasTechnical(): boolean {
    return !!this.analysis?.technical?.errorType;
  }

  get cause(): TransformerErrorFinding[] {
    return (this.analysis?.findings ?? []).filter(f => f.explainsError);
  }

  get otherFindings(): TransformerErrorFinding[] {
    return (this.analysis?.findings ?? []).filter(f => !f.explainsError);
  }

  /** The technical details as text: for the report to DTR and the copy button. Data
   * examples are not included. */
  get technicalText(): string {
    const t = this.analysis?.technical ?? {};
    const rule = this.analysis?.failedRule;
    return [
      `Transformer: ${this.analysis?.transformer ?? ''} (type ${t.transformType ?? this.analysis?.transformType ?? ''})`,
      `Rule: ${rule ? `${rule.position}${rule.ruleName ? ` '${rule.ruleName}'` : ''}` : 'none'}`,
      `Error: ${t.errorType ?? ''}: ${t.errorMessage ?? ''}`,
      '',
      t.trace ?? ''
    ].join('\n');
  }

  get reportLink(): string {
    const subject = `DTR engine issue: ${this.analysis?.transformer ?? 'transformer'}`;
    // Mail clients cap link length; the full details are also available via Copy.
    const body = this.technicalText.slice(0, 1500);
    return `mailto:${this.analysis?.reportTo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  async copyDetails(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.technicalText);
      this.copied = true;
    } catch {
      this.copied = false;
    }
  }
}
